import React, { FC, useContext, useEffect, useRef, useState } from "react";
import { SearchOutlined } from "@ant-design/icons";
import { Button, Flex, Input, TableProps, notification } from "antd";
import STableLayout, {
  DEFAULT_PAGE_SIZE,
  TableLayoutRef,
} from "@/styles/STableLayout";
import { orderList, orderCancel, ShipListParam, orderFetch } from "@/apis/shipment";
import { orderFetch as packOrderFetch } from "@/apis/packing";

import useDataList from "@/hooks/useDataList";
import { ColumnsType } from "antd/lib/table";
import { AnyObject } from "antd/lib/_util/type";
import { useTranslation } from "react-i18next";
import SText from "@/styles/SText";
import { Shipment } from "@/types/shipment";
import { CommonListParams } from "@/types/common";
import dayjs, { Dayjs } from "dayjs";
import { DatePicker } from "antd";
import SelectShipStatus from "@/components/Selector/SelectShipStatus";
import useShipmentColumns from "@/pages/Shipment/hooks/useShipmentColumns";
import AddOrderEdit from "./components/AddOrderEdit";
import { SConModal } from "@/styles/SConModal";
import { PackOrder } from "@/types/packing";
import { exportShipment } from "./components/utils";
import { DictContext } from "@/layout/context/DictContext";
import ExportProgressModal from "@/components/ExportProgressModal";

const { RangePicker } = DatePicker;

const ShipmentListPage: FC = () => {
  const { t } = useTranslation();
  const { currencys, exchangeRate } = useContext(DictContext);

  const tableRef = useRef<TableLayoutRef | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const [list, total, listLoading, { fetchList, addItem, updateItem, rmItem }] = useDataList<Shipment, [CommonListParams<ShipListParam>]>({
    fetchApi: orderList,
  });

  const [keyword, setKeyword] = useState<string>("");
  const [startTime, setStartTime] = useState<Dayjs | null>();
  const [endTime, setEndTime] = useState<Dayjs | null>();
  const [shipStatus, setShipStatus] = useState<number>();
  // const [openAdd, setOpenAdd] = useState(false);
  const [detail, setDetail] = useState<Shipment | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const [exportLoading, setExportLoading] = useState<boolean>(false);


  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    fetch(0);
  }, []);

  const handleSearch = () => {
    fetch(0);
  };

  const baseColumns = useShipmentColumns({ openDetail: setDetail });

  const handleCancel = async (record: Shipment) => {
    try {
      await orderCancel(record.id);
      rmItem(record.id);
      notification.success({ message: t('shipment.cancelOk') });
    } catch {
      notification.error({ message: t('shipment.cancelFAil') });
    }
  };

  const rowSelection: TableProps<AnyObject>["rowSelection"] = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
  };

  const handleExport = async () => {
    let selRow: Shipment[] = [];
    list.forEach(row => {
      if (selectedRowKeys.includes(row.id) && !!row.packingOrderId) {
        selRow.push(row);
      }
    });
    if (!!!selRow.length) return notification.error({ message: t('shipment.plSelShip') })
    // if (!!selRow.find(srow => srow.packingOrderId !== selRow[0].packingOrderId)) return notification.error({ message: t('shipment.plSelShipErr') })
    try {
      setExportLoading(true);
      let packOrderIds: string[] = [];
      let packOrders: PackOrder[] = [];
      selRow.forEach(row => {
        if (!packOrderIds.includes(row.packingOrderId!)) packOrderIds.push(row.packingOrderId!)
      })
      //获取装箱单
      for (let i = 0; i < packOrderIds.length; i++) {
        let packOrder = await packOrderFetch(packOrderIds[i]);
        !!packOrder && packOrders.push(packOrder);
      }
      //获取shipment
      let shipDtlList: Shipment[] = [];
      for (let i = 0; i < selRow.length; i++) {
        const shipData = await orderFetch(selRow[i].id);
        shipData && shipDtlList.push(shipData);
      }
      if (shipDtlList.length && packOrders.length) {
        try {
          await exportShipment(shipDtlList, packOrders, currencys, exchangeRate);
        } catch (error) {
          console.error(error);
          notification.error({ message: t('common.exportErr') });
        }
      }
      setExportLoading(false);
    } catch (error) {
      notification.error({ message: t('shipment.exportErr') })
    }
  };

  const columns: TableProps<Shipment>["columns"] = [
    ...baseColumns,
    {
      title: t("shipment.operation"),
      key: "operation",
      fixed: "right",
      width: 160,
      render: (_, record) => (
        <Flex gap={8}>
          <Button type="link" onClick={() => setDetail(record)}>
            {t("shipment.detail")}
          </Button>
          <SConModal
            title={t('common.tip')}
            content={t('shipment.confirmCancel', { no: record.number })}
            onOk={() => handleCancel(record)}
            okText={t('shipment.yes')}
            cancelText={t('shipment.no')}
          >
            <Button type="text" danger>
              {t("shipment.cancel")}
            </Button>
          </SConModal>
        </Flex>
      ),
    },
  ];

  const headerChildren = (
    <Flex justify="space-between" gap={10} align="center" style={{ width: "100%" }}>
      {/* <Button type="primary" onClick={() => setOpenAdd(true)}>{t("shipment.add")}</Button> */}
      <Button type="dashed" loading={exportLoading} onClick={() => handleExport()}>{t('warehouse.exportExcel')}</Button>
      <Flex gap={10}>
        <SelectShipStatus
          allowClear
          value={shipStatus}
          onChange={(val) => setShipStatus(val)}
          style={{ width: 180 }}
        />
        <RangePicker
          value={[startTime, endTime]}
          onChange={(val) => {
            if (val) {
              setStartTime(val[0]);
              setEndTime(val[1]);
            } else {
              setStartTime(null);
              setEndTime(null);
            }
          }}
        />
        <Input
          placeholder={t("shipment.plKeyword")}
          style={{ width: 200 }}
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          onPressEnter={handleSearch}
        />
        <Button
          type="primary"
          icon={<SearchOutlined />}
          onClick={handleSearch}
        />
      </Flex>
    </Flex>
  );

  const fetch = (offset: number) => {
    if (offset === 0) {
      tableRef?.current?.resetTablePage();
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    const take = tableRef.current?.pageSize ?? DEFAULT_PAGE_SIZE;

    fetchList({
      pagination: { offset, take },
      body: { keyword },
      ints: shipStatus !== undefined ? [shipStatus] : undefined,
      startTime: startTime ? startTime.startOf("day").valueOf() : undefined,
      endTime: endTime ? endTime.endOf("day").valueOf() : undefined,
      controller,
    }).catch((err) => {
      if (err?.name === 'AbortError') {
        return;
      }
    });
  };

  return (
    <Flex vertical style={{ width: "100%", height: "100%" }}>
      <STableLayout
        loading={listLoading}
        dataSource={list}
        columns={columns as ColumnsType<AnyObject>}
        rowSelection={rowSelection}
        headerChildren={headerChildren}
        tableLayoutRef={tableRef}
        total={total}
        handleChange={fetch}
        scrollX={1000}
        rowKey={'id'}
      />

      {/* {openAdd && <AddOrderEdit
        open={openAdd}
        formType="ADD"
        shipment={null}
        onClose={() => setOpenAdd(false)}
        onSuccess={(shipment) => {
          addItem(shipment);
          setOpenAdd(false);
        }}
      />} */}
      {detail &&
        <AddOrderEdit
          open={!!detail}
          formType="EDIT"
          shipment={detail}
          onClose={(info) => {
            if (info) {
              updateItem({
                ...detail,
                status: info.key,
              });
            }
            setDetail(null)
          }}
          onSuccess={(shipment) => {
            updateItem(shipment);
            setDetail(null);
          }}
          shipBoxes={null}
          packingOrderId={undefined}
          saleItems={null}
          purLotMap={null}
        />}
      <ExportProgressModal visible={exportLoading} progress={0} title={t('warehouse.exportExcel')} />
    </Flex>
  );
};

export default ShipmentListPage;
